import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {Backend, PERMISSION} from '@generated-backend-types';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {ActivatedRoute, Router} from '@angular/router';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {takeUntil} from 'rxjs';
import {take} from 'rxjs/operators';
import {EditModeEnum} from '../../../../enums/EditModeEnum';
import {BsModalService, ModalModule} from 'ngx-bootstrap/modal';
import {ConfirmDeleteModalComponent} from '../../../../components/confirm-delete-modal/confirm-delete-modal.component';
import {ProcessService} from '../../../../services/api/quality-management/process.service';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {
  MultiSelectEnumComponent
} from '../../../../components/form-inputs/multi-select-enum/multi-select-enum.component';
import {
  MultiSelectWithServiceListComponent
} from '../../../../components/form-inputs/multi-select-with-service-list/multi-select-with-service-list.component';
import {UserService} from '../../../../services/api/administration/user.service';
import {ReadOnlyStaticComponent} from '../../../../components/form-inputs/read-only-static/read-only-static.component';
import {DeleteCardComponent} from '../../../../components/form-inputs/delete-card/delete-card.component';
import {ProcessVersionService} from '../../../../services/api/quality-management/process-version.service';
import {AddableProcessesService} from '../../../../services/api/quality-management/addable-processes.service';
import {SubProcessesComponent} from './sub-processes/sub-processes.component';
import {StateCardComponent} from '../../../../components/state-card/state-card.component';
import {DocumentsComponent} from './documents/documents.component';
import {ProcessTabSwitcherComponent} from '../process-tab-switcher.component';
import {FormatInTimeZonePipe} from '../../../../pipes/format-in-time-zone.pipe';
import {TranslateArrayPipe} from '../../../../pipes/translate-array.pipe';
import {ParseIsoPipeModule} from 'ngx-date-fns';
import {de} from 'date-fns/locale';
import {FormSubmitService} from '../../../../services/form-submit.service';
import {IfHasAnyPermissionDirective} from '../../../../directives/if-has-any-permission.directive';
import Process = Backend.App.Resources.Dashboard.QualityManagement.Process;
import ProcessData = Backend.App.Data.Dashboard.QualityManagement.ProcessData;
import ProcessCategoryEnum = Backend.App.Enums.QualityManagement.ProcessCategoryEnum;
import ProcessPriorityEnum = Backend.App.Enums.QualityManagement.ProcessPriorityEnum;
import ProcessComplexityEnum = Backend.App.Enums.QualityManagement.ProcessComplexityEnum;
import ProcessRoleEnum = Backend.App.Enums.QualityManagement.ProcessRoleEnum;
import ProcessVersionData = Backend.App.Data.Dashboard.QualityManagement.ProcessVersionData;
import ModelStateEnum = Backend.App.Enums.ModelStateEnum;
import ProcessVersion = Backend.App.Resources.Dashboard.QualityManagement.ProcessVersion;

@Component({
  selector: 'app-authenticated-quality-management-processes-edit',
  templateUrl: './edit.component.html',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormGroupComponent,
    MultiSelectEnumComponent,
    MultiSelectWithServiceListComponent,
    ReadOnlyStaticComponent,
    DeleteCardComponent,
    StateCardComponent,
    DocumentsComponent,
    ProcessTabSwitcherComponent,
    FormatInTimeZonePipe,
    ParseIsoPipeModule,
    TranslateArrayPipe,
    SubProcessesComponent,
    IfHasAnyPermissionDirective,
    ModalModule,
  ],
  providers: [BsModalService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public process?: Process;
  public processVersion?: ProcessVersion;
  public mode = EditModeEnum.CREATE;
  public versionNumber = '';
  public publishedAt = '';

  public form: StrictFormGroup<ProcessData> = this.fb.group({
    state: this.fb.control(ModelStateEnum.PUBLISHED, [Validators.required]),
    ext_id: this.fb.control('', [Validators.required]),
    title: this.fb.control(''),
    category: this.fb.control<ProcessCategoryEnum | null>(null),
    roles: this.fb.control([]),
    description: this.fb.control(''),
    goal: this.fb.control(''),
    scope: this.fb.control(''),
    priority: this.fb.control<ProcessPriorityEnum | null>(null),
    complexity: this.fb.control<ProcessComplexityEnum | null>(null),
    owner_id: this.fb.control(''),
    review_cycle: this.fb.control<number | null>(null, [Validators.pattern("^[0-9-]*$"), Validators.min(1)]),
    input: this.fb.control(''),
    output: this.fb.control(''),
  });

  public publishVersionForm: StrictFormGroup<{ change_description: string }> = this.fb.group({
    change_description: this.fb.control('', [Validators.required]),
  });

  protected readonly ProcessCategoryEnum = ProcessCategoryEnum;
  protected readonly ProcessRoleEnum = ProcessRoleEnum;
  protected readonly ProcessPriorityEnum = ProcessPriorityEnum;
  protected readonly ProcessComplexityEnum = ProcessComplexityEnum;
  protected readonly PERMISSION = PERMISSION;
  protected readonly ModelStateEnum = ModelStateEnum;
  protected readonly EditModeEnum = EditModeEnum;
  protected readonly dfnsLocale = de;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: NonNullableFormBuilder,
    private readonly router: Router,
    public readonly userService: UserService,
    public readonly processService: ProcessService,
    public readonly addableProcessesService: AddableProcessesService,
    private readonly processVersionService: ProcessVersionService,
    private readonly formSubmitService: FormSubmitService,
    private readonly bsModalService: BsModalService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public get canBePublished() {
    return this.process !== undefined &&
      this.publishVersionForm.controls.change_description.value !== '' &&
      this.process.state !== ModelStateEnum.ARCHIVED &&
      this.mode === EditModeEnum.EDIT;
  }

  public get canBeDeleted() {
    return this.process !== undefined && this.process?.current_version === null;
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const process: Process | undefined = data['process'];
      let processVersion: ProcessVersion | undefined = data['processVersion'];

      if (!process) {
        this.publishVersionForm.disable();
        this.setEditMode(EditModeEnum.CREATE);
        return;
      }

      this.process = process;

      if (this.activatedRoute.snapshot.url.at(1)?.path === 'current') {
        this.setEditMode(EditModeEnum.SHOW);

        if (process?.current_version === undefined || process?.current_version === null) return;
        processVersion = process.current_version;
      }

      if (processVersion) {
        this.processVersion = processVersion;

        this.form.patchValue(processVersion);
        this.publishVersionForm.patchValue(processVersion);

        this.versionNumber = processVersion.version_number.toString();
        this.publishedAt = processVersion.created_at;

        this.setEditMode(EditModeEnum.SHOW);

        return;
      }

      if (this.process!.state === ModelStateEnum.ARCHIVED) {
        this.disablePublishVersionForm();
      }

      this.processVersionService.forProcess(process);
      this.addableProcessesService.forProcess(process);

      if (process.current_version) {
        this.versionNumber = (process.current_version.version_number + 1).toString();
      } else {
        this.versionNumber = '1';
      }

      this.form.patchValue(process);
      this.form.controls.ext_id.disable();

      this.setEditMode(EditModeEnum.EDIT);
    });
  }

  public save() {
    if (this.mode === EditModeEnum.SHOW) return;

    this.formSubmitService.submit<Process, ProcessData, ProcessService>({
      form: this.form,
      service: this.processService,
      modelId: this.process?.id,
      createPermission: PERMISSION.QUALITYMANAGEMENT_PROCESS_CREATE_ALL,
      updatePermission: PERMISSION.QUALITYMANAGEMENT_PROCESS_UPDATE_ALL,
      createSuccessMessage: this.translate.instant('authenticated.quality-management.processes.create.success'),
      updateSuccessMessage: this.translate.instant('authenticated.quality-management.processes.edit.success'),
      createErrorMessage: this.translate.instant('authenticated.quality-management.processes.create.error'),
      updateErrorMessage: this.translate.instant('authenticated.quality-management.processes.edit.error'),
    }).subscribe(process => {
      if (!process) return;
      this.process = process;

      if (this.process!.state === ModelStateEnum.ARCHIVED) {
        this.disablePublishVersionForm();
      }

      if (this.mode === EditModeEnum.CREATE) {
        this.router.navigateByUrl(`/quality-management/processes/${this.process.id}`).then();
      }

      this.mode = this.process?.id ? EditModeEnum.EDIT : EditModeEnum.CREATE;
      this.cdr.markForCheck();
    });
  }

  public publishVersion() {
    if (!this.canBePublished) return;

    const processVersionData = {
      ...this.form.value,
      ...this.publishVersionForm.value,
    };

    this.formSubmitService.submit<Process, ProcessData, ProcessService>({
      form: this.form,
      service: this.processService,
      modelId: this.process!.id,
      updatePermission: PERMISSION.QUALITYMANAGEMENT_PROCESS_UPDATE_ALL,
    }).subscribe(process => {
      if (!process) return;

      this.formSubmitService.submit<ProcessVersion, ProcessVersionData, ProcessVersionService>({
        form: this.publishVersionForm,
        payload: processVersionData as ProcessVersionData,
        service: this.processVersionService,
        createPermission: PERMISSION.QUALITYMANAGEMENT_PROCESSVERSION_CREATE_ALL,
        createSuccessMessage: this.translate.instant('authenticated.quality-management.processes.versions.create.success'),
        createErrorMessage: this.translate.instant('authenticated.quality-management.processes.versions.create.error'),
      }).subscribe(processVersion => {
        if (!processVersion) return;

        this.publishVersionForm.controls.change_description.setValue('');
        this.publishVersionForm.markAsPristine();
        this.publishVersionForm.markAsUntouched();

        this.versionNumber = (processVersion.version_number + 1).toString();
        this.processService.get(this.process!.id, true).subscribe(res => {
          this.process = res.data;
          this.cdr.markForCheck();
        });

        this.cdr.markForCheck();
      });
    });
  }

  public delete() {
    if (this.mode !== EditModeEnum.EDIT || !this.process) return;

    this.bsModalService.show(ConfirmDeleteModalComponent, {
      initialState: {
        modelId: this.process.id,
        dialogTranslation: this.translate.instant(
          'authenticated.quality-management.processes.delete.confirm_title',
          {itemName: this.process.title ?? this.process.ext_id},
        ),
        successTranslation: this.translate.instant(
          'authenticated.quality-management.processes.delete.success',
        ),
        errorTranslation: this.translate.instant(
          'authenticated.quality-management.processes.delete.error',
        ),
        permissions: [PERMISSION.QUALITYMANAGEMENT_PROCESS_DELETE_ALL],
        observable: this.processService.delete(this.process.id),
      },
    }).content?.deleteSuccess
      .pipe(takeUntil(this.onDestroy$), take(1))
      .subscribe(() => {
        this.router.navigateByUrl('/quality-management/processes').then();
      });
  }

  private setEditMode(mode: EditModeEnum) {
    this.mode = mode;
    this.titleService.setTitle('authenticated.quality-management.processes.' + mode + '.title');

    if (mode === EditModeEnum.SHOW) {
      this.form.disable();
      this.publishVersionForm.disable();
    }

    this.cdr.markForCheck();
  }

  private disablePublishVersionForm() {
    this.publishVersionForm.controls.change_description.setValue('');
    this.publishVersionForm.markAsPristine();
    this.publishVersionForm.markAsUntouched();
    this.publishVersionForm.disable();
  }
}
